import PropTypes from 'prop-types';
import format from '../../../../utils/format';
import CardModule from '../../shared/card-module';
import useHypotheticalGrowthStore from '../.././../../stores/profile/modules/use-hypothetical-growth';
import DropdownUnderline from '../../../atoms/dropdown-underline';
import TableBasic from '../../../atoms/table-basic';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import ButtonSwitch from '../../../atoms/button-switch';
import styles from './hypothetical-growth.module.scss';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import BsButton from 'react-bootstrap/Button';
import Carousel from 'react-bootstrap/Carousel';
import Icon from '../../../atoms/icon';
import isEmpty from 'lodash/isEmpty';
import CrosshairTooltips from '../../../atoms/chart-crosshair-tooltips';
import moment from 'moment-msdate';
import { utils, writeFile } from 'xlsx';
import chartLineColorsByColorCode from '../../../../helpers/statics/chart-color';

const className = augmentClassName(styles);

const HypotheticalGrowth = ({ showPriceChartOnly }) => {
  const {
    chartHeight,
    chartSelected,
    chartTypes,
    closeProximityDisclosure,
    currentTimeframeOption,
    dropdownItems,
    endDate,
    fundsData = [{}],
    isHypo10k,
    hypoChartObject = {},
    onChartChange,
    onChangeTimeframe,
    desktopChartData,
    priceDailyDataForXLS,
    mobileChartData,
    priceData,
    priceChartLastClose,
    priceChartObject = {},
    chartTableIndex,
    quoteData,
    screenReaderTableData = {},
    showAsTable,
    handleChartTableIndexSelect,
    onShowAsTableClick
  } = useHypotheticalGrowthStore({ showPriceChartOnly });

  const priceChartFundName = quoteData && quoteData.isLoaded ? quoteData.data.fundName : undefined;
  const chartTableData = window.innerWidth > 767 ? desktopChartData : mobileChartData;
  const isTableHasValues = chartTableData && !isEmpty(chartTableData) && chartTableData.length > 0;
  const hidePriceChartElements = !(!isHypo10k && priceChartLastClose);
  const ChartLegend = ({ fundName, value, labelColor }) => {
    return (
      <div {...className('d-inline-block', 'mr-3')}>
        <div {...className('label-dot', 'mr-2', `bg-${labelColor}`)} />
        <div {...className('d-inline-block', 'fund-names', 'text-muted')}>{fundName}</div>
        <div {...className('ml-4', 'fund-value', 'font-weight-bold', `color-${labelColor}`)}>
          {format.price(value, { formatString: '$0,0' }, true)}
        </div>
      </div>
    );
  };

  const exportToExcelByXlsx = chartTabledataList => {
    const data = [];
    chartTabledataList.flat(2).forEach(subArray => {
      subArray?.data?.forEach(item => {
        data.push({
          Date: item.date,
          Price: format.price(item.value || item.close)
        });
      });
    });
    const workbook = utils.book_new();
    const worksheet = utils.json_to_sheet(data);
    utils.book_append_sheet(workbook, worksheet, 'Daily Performance');
    writeFile(workbook, 'Investment Details.xls');
  };

  ChartLegend.propTypes = {
    fundName: PropTypes.string,
    value: PropTypes.number,
    labelColor: PropTypes.string
  };

  const PriceChartLegend = ({ fundName, value, labelColor, closeDate }) => {
    return (
      <div {...className('d-inline-block', 'mr-3')}>
        <div {...className('label-dot', 'mr-2', `bg-${labelColor}`)} />
        <div {...className('d-inline-block', 'fund-names', 'text-muted')}>{fundName}</div>
        <div {...className('ml-4', 'fund-value', 'font-weight-bold', `color-${labelColor}`)}>
          <span {...className('chart-info-text-space')}>{format.date(new Date(closeDate))}</span>

          <span>Close: </span>
          {format.price(value, { formatString: '0.00' }, true)}
        </div>
      </div>
    );
  };

  PriceChartLegend.propTypes = {
    fundName: PropTypes.string,
    value: PropTypes.number,
    labelColor: PropTypes.string,
    closeDate: PropTypes.string
  };

  const HypoCardDropDown = ({ dropDownClass }) => {
    return (
      <div {...className('float-right', `${dropDownClass}`)}>
        <DropdownUnderline
          items={dropdownItems}
          label="Change time frame"
          onChange={onChangeTimeframe}
          selectedItem={currentTimeframeOption}
        />
      </div>
    );
  };

  const isShowAsTable = () => {
    if (isTableHasValues) {
      return showAsTable ? 'd-block' : 'd-none';
    }

    return showAsTable ? '' : 'd-none';
  };

  HypoCardDropDown.propTypes = {
    dropDownClass: PropTypes.string
  };

  return (
    <CardModule
      hypo10k
      {...className('card-height')}
      id={'hypo-card'}
      data-analytics-componenttype="interactive-graphic-component"
      data-analytics-id="IG_HypotheticalGrowth"
      data-analytics-section="component"
    >
      <CardModule.Header>
        <Row>
          <Col md={4} lg={6} className={'md-col-print-12'}>
            {isHypo10k ? (
              <div {...className('font-family-roboto', 'font-weight-bold', 'font-size-18')}>
                Hypothetical Growth of $10k
                <span
                  {...className(
                    'ml-2',
                    'font-family-roboto',
                    'font-weight-normal',
                    'text-gray-500',
                    'font-size-12'
                  )}
                >
                  As of {format.date(endDate)}
                </span>
              </div>
            ) : (
              'Price Chart'
            )}
          </Col>
          <Col md={8} lg={6}>
            {!showPriceChartOnly ? (
              <ButtonSwitch
                options={chartTypes}
                onChange={onChartChange}
                selected={chartSelected}
              />
            ) : (
              <HypoCardDropDown dropDownClass={'dropdown-spacing'} />
            )}
          </Col>
        </Row>
      </CardModule.Header>
      <CardModule.Body>
        <Row>
          {isHypo10k && !showAsTable ? (
            <Col>
              {fundsData.map((fund, index) => {
                return (
                  <ChartLegend
                    data-key={`${fund.name || ''}-${index}`}
                    fundName={fund.legendName}
                    key={`${fund.name || ''}-${index}`}
                    labelColor={chartLineColorsByColorCode[fund.chartColor]}
                    value={fund.value}
                  />
                );
              })}
            </Col>
          ) : undefined}
          {!isHypo10k && !showAsTable && priceChartLastClose ? (
            <Col>
              <PriceChartLegend
                fundName={priceChartFundName}
                value={priceChartLastClose.value}
                labelColor={'blue'}
                closeDate={priceChartLastClose.date}
              />
            </Col>
          ) : undefined}
          {showPriceChartOnly ? undefined : (
            <Col md={showAsTable ? 12 : 3}>
              <HypoCardDropDown />
            </Col>
          )}
        </Row>
        <div {...className('mb-5', 'mt-5', 'position-relative', 'chart-table-container')}>
          <>
            <div
              {...className(
                showAsTable ? 'd-none' : 'd-block',
                'print-full-width',
                'print-chart-container'
              )}
            >
              <div
                style={{ height: chartHeight }}
                id="hypo-chart-target"
                {...className(!isHypo10k ? 'd-none' : '', 'print-full-width')}
              ></div>

              <div
                style={{ height: chartHeight }}
                id="price-chart-target"
                {...className(isHypo10k ? 'd-none' : '', 'print-full-width')}
              ></div>
              <div {...className(hidePriceChartElements ? 'd-none' : '')}></div>
              {isHypo10k ? (
                <CrosshairTooltips chart={hypoChartObject} chartType="hypoChart" />
              ) : (
                <CrosshairTooltips chart={priceChartObject} chartType="priceChart" />
              )}
            </div>
            {isTableHasValues ? (
              <div {...className(isShowAsTable())}>
                <Carousel
                  {...className('chart-table-carousel')}
                  interval={null}
                  indicators={true}
                  activeIndex={chartTableIndex}
                  onSelect={handleChartTableIndexSelect}
                  nextIcon={<Icon.ChevronThick {...className('flip', 'nextIcon')} />}
                  prevIcon={<Icon.ChevronThick />}
                  wrap={false}
                >
                  {chartTableData.map((item, index) => (
                    <Carousel.Item
                      data-key={`${item.length || ''}-${index}`}
                      key={`${item.length || ''}-${index}`}
                    >
                      <Row>
                        {item.map((column, columnIndex) => (
                          <Col
                            data-key={`${column.title || ''}-${columnIndex}`}
                            key={`${column.title || ''}-${columnIndex}`}
                            {...className('carousel-column')}
                          >
                            <div {...className('chart-table')}>
                              <TableBasic flush>
                                <thead>
                                  <tr>
                                    <th {...className('text-left')}>{column.title}</th>
                                    <th {...className('text-right')}>
                                      {isHypo10k ? 'Balance' : 'Price'}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {column.data.map((row, rowIndex) => (
                                    <tr
                                      data-key={`${row.date || ''}-${rowIndex}`}
                                      key={`${row.date || ''}-${rowIndex}`}
                                    >
                                      <td {...className('text-left')}>
                                        {format.date(moment(row.date))}
                                      </td>
                                      <td {...className('text-right', 'font-weight-bold')}>
                                        {format.price(row.value || row.close)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </TableBasic>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
            ) : (
              <div
                style={{ height: chartHeight }}
                {...className(isShowAsTable(), 'no-table-data-container')}
              >
                Table not available
              </div>
            )}
          </>

          <div {...className('sr-only')}>
            <TableBasic>
              <caption>Hypothetical growth table</caption>
              <thead>
                <tr>
                  <th {...className('text-left')}>Date</th>
                  {screenReaderTableData.headers &&
                    screenReaderTableData.headers.map((header, index) => (
                      <th
                        data-key={`${header || ''}-${index}`}
                        key={`${header || ''}-${index}`}
                        {...className('text-left')}
                      >
                        {format.text(header)}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {screenReaderTableData.rows &&
                  screenReaderTableData.rows.map((row, index) => (
                    <tr data-key={`${row.date || ''}-${index}`} key={`${row.date || ''}-${index}`}>
                      <th scope="row" {...className('text-left')}>
                        {row.date}
                      </th>
                      <td>{format.number(row.value)}</td>
                    </tr>
                  ))}
              </tbody>
            </TableBasic>
          </div>
          <div {...className('sr-only')}>
            <TableBasic>
              <caption>Price chart</caption>
              <thead>
                <tr>
                  <th {...className('text-left')}>Date</th>
                  <th {...className('text-left')}>{priceChartFundName}</th>
                </tr>
              </thead>
              <tbody>
                {priceData &&
                  priceData.map((row, index) => (
                    <tr data-key={`${row.date || ''}-${index}`} key={`${row.date || ''}-${index}`}>
                      <th scope="row" {...className('text-left')}>
                        {row.date}
                      </th>
                      <td>{format.number(row.close)}</td>
                    </tr>
                  ))}
              </tbody>
            </TableBasic>
          </div>
        </div>
        <div {...className('my-3', 'd-print-none')}>
          <BsButton
            {...className('p-0', 'font-size-10', 'text-black')}
            variant="link--reverse-underline"
            size="sm"
            onClick={() => onShowAsTableClick(!showAsTable)}
          >
            {showAsTable ? 'Show as chart' : 'Show as table'}
          </BsButton>

          {!isHypo10k && isTableHasValues && showAsTable && (
            <BsButton
              {...className('float-right', 'p-0', 'font-size-10', 'text-black')}
              variant="link--reverse-underline"
              size="sm"
              onClick={() => exportToExcelByXlsx(priceDailyDataForXLS)}
            >
              {'export to XLS'}
            </BsButton>
          )}
        </div>
        <p {...className('paragraph-standard', 'mb-0', 'disclaimer-text')}>
          {isHypo10k && closeProximityDisclosure}
        </p>
      </CardModule.Body>
    </CardModule>
  );
};

HypotheticalGrowth.propTypes = {
  onClickDisclosures: PropTypes.func,
  showPriceChartOnly: PropTypes.bool,
  isPrinting: PropTypes.bool
};

export default HypotheticalGrowth;
